import React, { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import api, { formatApiError, API, getAuthToken } from "@/lib/api";
import { toast } from "sonner";
import {
  Search,
  Filter,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  CheckCircle2,
  XCircle,
  CircleDot,
} from "lucide-react";

/**
 * v1.2 PR-B.1 — Cross-org notifications log viewer.
 *
 * Every email / SMS / Slack / Teams / in-app send across every org, filterable
 * by channel, status, recipient, kind, and date range.
 *
 * Backend: GET /api/super-admin/notifications-log?org_id&channel&status&recipient&kind&from_date&to_date&limit&offset
 *   - Returns array body, pagination via X-Total-Count response header.
 *
 * CSV export: GET /api/super-admin/notifications-log/export.csv?...filters (50k cap).
 */
const PAGE_SIZE = 50;
const CHANNELS = ["email", "sms", "slack", "teams", "inapp", "push"];
const STATUSES = ["sent", "mock_send", "failed", "fallback"];

export default function SuperAdminNotificationsLogCrossOrg() {
  const [orgs, setOrgs] = useState([]);
  const [filters, setFilters] = useState({
    org_id: "", channel: "", status: "", recipient: "",
    kind: "", from_date: "", to_date: "",
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);

  useEffect(() => {
    api.get("/super-admin/orgs").then(({ data }) => setOrgs(data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/super-admin/notifications-log", { params });
      setRows(res.data || []);
      const t = parseInt(res.headers?.["x-total-count"] || "0", 10);
      setTotal(Number.isFinite(t) ? t : 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filters, offset]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const url = `${API}/super-admin/notifications-log/export.csv?${qs.toString()}`;
    const token = getAuthToken();
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`);
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `notifications-log-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("CSV exported");
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <SuperAdminLayout>
      <div data-testid="super-admin-notifications-log-page" className="space-y-5">
        <header className="flex items-end justify-between gap-4">
          <div>
            <div className="eb-overline mb-1">Super admin</div>
            <h1 className="text-3xl font-semibold text-navy-900 dark:text-white inline-flex items-center gap-2">
              <ListChecks className="w-7 h-7 text-orange-500" /> Notifications log (cross-org)
            </h1>
            <p className="text-sm text-navy-500 dark:text-navy-300 mt-1 max-w-3xl">
              Every send across every org. For failed sends, the row drawer surfaces
              the provider error summary.
            </p>
          </div>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium bg-orange-500 text-white hover:bg-orange-600"
            data-testid="notif-export-csv"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV ({total.toLocaleString()})
          </button>
        </header>

        <FilterBar
          orgs={orgs}
          filters={filters}
          onChange={(next) => { setOffset(0); setFilters({ ...filters, ...next }); }}
          onClear={() => { setOffset(0); setFilters({ org_id: "", channel: "", status: "", recipient: "", kind: "", from_date: "", to_date: "" }); }}
        />

        <Table
          rows={rows} loading={loading} total={total} offset={offset}
          onPage={setOffset} onRowClick={setDrawer}
        />
      </div>
      <Drawer row={drawer} onClose={() => setDrawer(null)} />
    </SuperAdminLayout>
  );
}

function FilterBar({ orgs, filters, onChange, onClear }) {
  const [local, setLocal] = useState(filters);
  useEffect(() => setLocal(filters), [filters]);
  const submit = (e) => { e?.preventDefault(); onChange(local); };
  return (
    <form
      onSubmit={submit}
      className="bg-white border border-navy-100 rounded-xl p-4 grid gap-3 md:grid-cols-7 items-end"
      data-testid="notif-filter-bar"
    >
      <div>
        <label className="eb-label">Organisation</label>
        <select className="eb-input" value={local.org_id}
                onChange={(e) => setLocal({ ...local, org_id: e.target.value })}
                data-testid="notif-filter-org">
          <option value="">All</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div>
        <label className="eb-label">Channel</label>
        <select className="eb-input" value={local.channel}
                onChange={(e) => setLocal({ ...local, channel: e.target.value })}
                data-testid="notif-filter-channel">
          <option value="">All</option>
          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="eb-label">Status</label>
        <select className="eb-input" value={local.status}
                onChange={(e) => setLocal({ ...local, status: e.target.value })}
                data-testid="notif-filter-status">
          <option value="">All</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="eb-label">Recipient (substring)</label>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-navy-400 absolute left-2.5 top-3" />
          <input className="eb-input pl-8" value={local.recipient}
                 onChange={(e) => setLocal({ ...local, recipient: e.target.value })}
                 placeholder="e.g. @example.com"
                 data-testid="notif-filter-recipient" />
        </div>
      </div>
      <div>
        <label className="eb-label">From date</label>
        <input type="date" className="eb-input" value={local.from_date}
               onChange={(e) => setLocal({ ...local, from_date: e.target.value })}
               data-testid="notif-filter-from" />
      </div>
      <div>
        <label className="eb-label">To date</label>
        <input type="date" className="eb-input" value={local.to_date}
               onChange={(e) => setLocal({ ...local, to_date: e.target.value })}
               data-testid="notif-filter-to" />
      </div>
      <div className="md:col-span-7 flex justify-end gap-2">
        <button type="button" onClick={onClear}
                className="text-xs px-3 py-1.5 rounded-md border border-navy-200 hover:bg-navy-50"
                data-testid="notif-filter-clear">Clear</button>
        <button type="submit"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-navy-900 text-white hover:bg-navy-800"
                data-testid="notif-filter-apply">
          <Filter className="w-3.5 h-3.5" /> Apply filters
        </button>
      </div>
    </form>
  );
}

function StatusPill({ status, mode }) {
  let cls = "bg-navy-50 text-navy-500 border-navy-200";
  let Icon = CircleDot;
  if (status === "sent") { cls = "bg-success/10 text-success border-success/30"; Icon = CheckCircle2; }
  else if (status === "mock_send") { cls = "bg-warning/10 text-warning border-warning/30"; Icon = CircleDot; }
  else if (status === "failed") { cls = "bg-danger/10 text-danger border-danger/30"; Icon = XCircle; }
  else if (status === "fallback") { cls = "bg-warning/10 text-warning border-warning/30"; Icon = XCircle; }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}>
      <Icon className="w-3 h-3" /> {status || "—"}
      {mode === "live" ? <span className="ml-1 text-[9px] opacity-70">live</span> : null}
    </span>
  );
}

function Table({ rows, loading, total, offset, onPage, onRowClick }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const current = Math.floor(offset / PAGE_SIZE) + 1;
  return (
    <div className="bg-white border border-navy-100 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="notif-table">
          <thead className="bg-navy-50/60 text-xs text-navy-600 uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Channel</th>
              <th className="text-left px-4 py-2">Recipient</th>
              <th className="text-left px-4 py-2">Subject</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Org</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-100">
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-navy-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-navy-400">No notifications match.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} onClick={() => onRowClick(r)}
                  className="cursor-pointer hover:bg-navy-50/40 transition-colors"
                  data-testid="notif-row">
                <td className="px-4 py-2 text-xs font-mono text-navy-700 whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-2 text-xs text-navy-700">{r.channel || r.service || "—"}</td>
                <td className="px-4 py-2 text-xs text-navy-700 truncate max-w-[200px]">{r.recipient || <span className="text-navy-300">—</span>}</td>
                <td className="px-4 py-2 text-xs text-navy-800 truncate max-w-[300px]">{r.subject || <span className="text-navy-300">—</span>}</td>
                <td className="px-4 py-2"><StatusPill status={r.status} mode={r.mode} /></td>
                <td className="px-4 py-2 text-xs text-navy-500 truncate max-w-[160px]">
                  {r.org_id ? r.org_id.slice(0, 8) + "…" : <span className="text-navy-300">(global)</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <footer className="px-4 py-3 border-t border-navy-100 flex items-center justify-between text-xs text-navy-500" data-testid="notif-pagination">
        <span>Page {current} of {pages} · {total.toLocaleString()} rows</span>
        <div className="inline-flex gap-1">
          <button disabled={offset <= 0} onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}
                  className="px-2 py-1 rounded border border-navy-200 hover:bg-navy-50 disabled:opacity-40"
                  data-testid="notif-page-prev">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button disabled={offset + PAGE_SIZE >= total} onClick={() => onPage(offset + PAGE_SIZE)}
                  className="px-2 py-1 rounded border border-navy-200 hover:bg-navy-50 disabled:opacity-40"
                  data-testid="notif-page-next">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </footer>
    </div>
  );
}

function sanitisePayload(payload) {
  // Defensive: strip anything that LOOKS like a secret. Server-side already
  // never logs raw secrets, but as a belt-and-braces measure we redact common
  // shapes on display too.
  if (!payload || typeof payload !== "object") return payload;
  const cp = {};
  Object.entries(payload).forEach(([k, v]) => {
    if (/key|token|secret|password|authorization/i.test(k)) {
      cp[k] = typeof v === "string" && v.length > 4 ? `***${v.slice(-4)}` : "***";
    } else {
      cp[k] = v;
    }
  });
  return cp;
}

function Drawer({ row, onClose }) {
  if (!row) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="notif-drawer">
      <button onClick={onClose} className="flex-1 bg-black/40" aria-label="Close drawer" />
      <aside className="w-full md:w-[640px] bg-white shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-white px-5 py-4 border-b border-navy-100 flex items-center justify-between">
          <div>
            <div className="eb-overline">Notification</div>
            <h2 className="text-sm font-medium text-navy-900 truncate max-w-[420px]">
              {row.subject || row.kind || row.channel || "—"}
            </h2>
          </div>
          <button onClick={onClose} className="text-navy-400 hover:text-navy-700">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="flex gap-3 items-center"><StatusPill status={row.status} mode={row.mode} /> <span className="text-xs text-navy-500">{row.latency_ms != null ? `${row.latency_ms} ms` : ""}</span></div>
          <div><div className="eb-overline mb-0.5">When</div><div className="text-navy-900">{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</div></div>
          <div><div className="eb-overline mb-0.5">Channel</div><div className="font-mono text-xs text-navy-900">{row.channel || row.service || "—"}</div></div>
          <div><div className="eb-overline mb-0.5">Recipient</div><div className="font-mono text-xs text-navy-900 break-all">{row.recipient || "—"}</div></div>
          <div><div className="eb-overline mb-0.5">Provider id</div><div className="font-mono text-xs text-navy-900 break-all">{row.provider_id || "—"}</div></div>
          <div><div className="eb-overline mb-0.5">Org</div><div className="font-mono text-xs text-navy-900 break-all">{row.org_id || "(global)"}</div></div>
          {row.error_msg && (
            <div className="bg-danger/5 border border-danger/30 rounded p-3" data-testid="notif-drawer-error">
              <div className="eb-overline mb-1 text-danger">Provider error summary</div>
              <div className="text-xs font-mono text-danger break-all">{row.error_msg}</div>
            </div>
          )}
          <div>
            <div className="eb-overline mb-1">Payload (sanitised)</div>
            <pre className="bg-navy-50 border border-navy-100 rounded p-3 text-xs text-navy-800 overflow-x-auto">
              {JSON.stringify(sanitisePayload(row.payload || {}), null, 2)}
            </pre>
          </div>
          {row.body && (
            <div>
              <div className="eb-overline mb-1">Body preview</div>
              <pre className="bg-navy-50 border border-navy-100 rounded p-3 text-xs text-navy-800 overflow-x-auto whitespace-pre-wrap">
                {String(row.body).slice(0, 4000)}
              </pre>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
